<template>
    <div class="page-login">
        <div class="block-title">
            Вход в систему
            <Loader v-show="pending" class="loader" />
        </div>

        <div v-if="message" class="message error">
            <div class="text error" v-html="message" />
        </div>

        <div class="notification">
            <div class="text">
                <div class="description">
                    Если есть учётная запись в домене ПАО «Ростелеком», то необходимо продолжить вход с этими данными
                </div>
            </div>
        </div>

        <div class="form-field">
            <div class="title">Email пользователя</div>
            <div class="value">
                <input v-model="v.email.$model"
                       :class="{ error: v.email.$error }"
                       data-test-id="input-email"
                       placeholder="Введите адрес электронной почты"
                       autofocus
                       class="email"
                       type="email"
                       @keyup.enter="enterLogin"/>
            </div>
        </div>
        <div class="form-field">
            <div class="title">Пароль</div>
            <div class="value">
                <password ref="password"
                          v-model="v.password.$model"
                          :a-class="{ error: v.password.$error }"
                          data-test-id="input-password"
                          class="password"
                          placeholder="Введите пароль"
                          @enter="enterLogin"/>

                <TooltipOld :opened="passwordLdapError"
                            bind-to=".password input"
                            class="error"
                            closeable>
                    Введите пароль от доменной учетной записи
                </TooltipOld>
            </div>
            <div class="additional two-sided">
                <router-link class="accent left" to="/user/restore">Забыли пароль?</router-link>
                <div class="right">
                    <Checkbox v-model="remember" class="right">Запомнить меня</Checkbox>
                </div>
            </div>
        </div>
        <div class="form-field form-action">
            <div class="value">
                <button :disabled="v.$invalid || pending"
                        class="primary fullwidth action"
                        @click="login">Войти в систему
                </button>
            </div>
        </div>
        <div class="form-field form-action signup">
            <div class="value">
                <router-link class="fullwidth action" tag="button" to="/user/signup">Зарегистрироваться
                </router-link>
            </div>
        </div>
    </div>
</template>

<script>
import { isLdapEmail } from '@/common/allowed.emails.js';
import { SEARCH_HIST_KEY } from '@/common/page_comp/SearchProduct/config.history.js';
import { currentUser, updateSession } from '@/common/session.js';
import { isMobile } from '@/common/util/util.js';
import { emailRulesDefault } from '@/common/util/validations/email.js';
import { requiredRule } from '@/common/util/validations/main.js';
import { login } from '@/restapi/user/user.js';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { showConfirm } from '@dsee/components/popups/confirm.js';
import local from '../../../common/util/local.js';
import IconError from '../icons/error.svg?component';
import IconWarning from '../icons/warning.svg?component';

const lastEmailKey = 'LAST_EMAIL';
const lastEmail = local.get(lastEmailKey, '');

export default {
    components: { IconError, IconWarning },
    setup() {
        return {
            v: useVuelidate(),
        };
    },
    computed: {
        passwordLdapError() {
            if (!isLdapEmail(this.email))
                return false;

            return this.message === 'Неверное имя пользователя или пароль';
        },
        mobile() {
            return isMobile();
        },
    },
    data() {
        return {
            email: '',
            password: '',
            remember: false,
            message: '',

            pending: false,
        };
    },
    validations() {
        return {
            email: {
                ...requiredRule(),
                ...emailRulesDefault(),
            },
            password: { required },
        };
    },
    created() {
        const user = currentUser();
        if (this.$route.query?.redirect && !user.id && lastEmail.length) {
            this.message = 'Сессия истекла';
        }
    },
    mounted() {
        if (lastEmail.length) {
            this.email = lastEmail;
            this.$refs.password.$el.querySelector('input').focus();
        }
    },
    methods: {
        async login() {
            this.v.$touch();

            if (this.v.$invalid)
                return;

            try {
                this.message = '';
                this.pending = true;

                const user = await login({
                    email: this.email,
                    password: this.password,
                    remember: this.remember,
                });

                updateSession(user);
                local.put(lastEmailKey, user.email);

                await this.redirect();
            } catch (e) {
                this.message = e.message;
            } finally {
                this.pending = false;
            }
        },
        enterLogin() {
            if (!this.v.$invalid)
                this.login();
        },
        async redirect() {
            // намекнуть переключиться в мобильном браузере на версию для ПК
            if (this.mobile) {
                const resp = await showConfirm({
                    title: 'Рекомендуем переключиться на версию для ПК',
                    text: 'Для более удобного использования портала в мобильном браузере рекомендуем переключиться на версию для ПК',
                    buttons: [
                        {
                            title: 'Закрыть',
                            value: 1,
                            class: 'primary',
                        },
                    ],
                });
            }

            const redirect = this.$route.query?.redirect || '/';
            await this.$router.push(redirect);
        },
        deleteSearchHistory() {
            local.remove(SEARCH_HIST_KEY);
        },
    },
};
</script>

<style lang="scss" scoped>
.page-login {
    .signup {
        margin-top: 12px;

        .action {
            display: block;
        }
    }
}
</style>
